import { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Chip, FormLabel, Stack } from '@mui/material';
import { format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  AutoMatchingFiltersFragment,
  Property_Searches_Bool_Exp,
} from '@graphql/__generated__/graphql';
import { getDefaultBuyersMatchingFilters } from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import {
  type QuickFilterDefinition,
  addWhereClause,
} from '@modules/_shared/components/filters/Filters';
import { getBooleanDefaultValue } from '@modules/_shared/components/filters/quick-filters/BooleanFilter';
import { extractDateRangeValues } from '@modules/_shared/components/filters/quick-filters/DateFilter';
import { getNumberDefaultValue } from '@modules/_shared/components/filters/quick-filters/NumberFilter';
import { extractPlacesSelectedIds } from '@modules/_shared/components/filters/quick-filters/PlaceFilter';
import { useStringFilterTranslation } from '@modules/_shared/components/filters/quick-filters/StringFilter';
import { SchemaProvider } from '@modules/_shared/components/filters/SchemaProvider';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { usePropertySearchQuickFilters } from '@modules/_shared/components/property-search/usePropertySearchQuickFilters';
import { getDictionaryLabel } from '@modules/_shared/utils/dictionaries';
import { useLocale } from '@shared/hooks/locale';

export const AUTO_MATCHING_FILTERS_FRAGMENT = gql(/* GraphQL */ `
  fragment AutoMatchingFilters on lots {
    id
    auto_matching_filters
    ...DefaultBuyersMatchingFilters
  }
`);

const GET_AUTO_FILTER_PLACES = gql(/* GraphQL */ `
  query GetAutoFilterPlaces($ids: [uuid!]!) {
    places(where: { id: { _in: $ids } }, order_by: { label: asc }) {
      id
      name
      label
      label_en
    }
  }
`);

const GET_AUTO_FILTER_USERS = gql(/* GraphQL */ `
  query GetAutoFilterUsers($ids: [uuid!]!) {
    selected_users: users(where: { id: { _in: $ids } }) {
      id
      full_name
    }
  }
`);

const GET_AUTO_FILTER_TEAMS = gql(/* GraphQL */ `
  query GetAutoFilterTeams($ids: [uuid!]!) {
    selected_teams: teams(where: { id: { _in: $ids } }) {
      id
      name
    }
  }
`);

const GET_AUTO_FILTER_ENUMS = gql(/* GraphQL */ `
  query GetAutoFilterEnums {
    dictionaries(where: { type: { _eq: enums } }) {
      name
      label
    }
  }
`);

const filterChipSx = {
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 2,
  '&:hover': { background: 'var(--mui-palette-background-paper)' },
  '&.MuiChip-root': { background: 'var(--mui-palette-background-paper)' },
  '&.MuiChip-root:hover': { background: 'var(--mui-palette-background-paper)' },
} as const;

// Typing for the jsonb column.
type AutoMatchingFilters = Property_Searches_Bool_Exp;

type SearchQuickFilter = QuickFilterDefinition;

type AutoMatchingFiltersProps = {
  listing: AutoMatchingFiltersFragment;
  extendedPlaces?: string[];
  readOnly?: boolean;
};

export const AutoMatchingFilters: React.FC<AutoMatchingFiltersProps> = ({
  listing,
  extendedPlaces,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const allFilters = usePropertySearchQuickFilters({
    disableOfferType: true,
  });

  const hasSelectedCriteria = useMemo(() => {
    return Object.keys(listing.auto_matching_filters ?? {}).length > 0;
  }, [listing.auto_matching_filters]);

  const activeFilters = useMemo(() => {
    return hasSelectedCriteria
      ? listing.auto_matching_filters
      : getDefaultBuyersMatchingFilters(listing, extendedPlaces);
  }, [listing, hasSelectedCriteria, extendedPlaces]);

  const handleEditCriteria = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(
      'auto-matching_where',
      JSON.stringify(activeFilters ?? {}),
    );
    setSearchParams(newSearchParams);
    navigate({
      pathname: './filters',
      search: newSearchParams.toString(),
    });
  }, [activeFilters, searchParams, setSearchParams, navigate]);

  return (
    <SchemaProvider>
      <Box>
        <FormLabel
          sx={{ marginBottom: '0.5rem', fontWeight: 500, display: 'block' }}
        >
          {t('Criteria')}
        </FormLabel>
        {!hasSelectedCriteria && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              border: theme => `1px solid ${theme.palette.info.main}`,
            }}
          >
            {t(
              'No custom criteria provided, using the default criteria instead.',
            )}
          </Alert>
        )}
        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {allFilters?.map(filter => (
            <AutoMatchingChip
              key={filter.path.join('.')}
              filter={filter}
              where={activeFilters}
            />
          ))}
        </Stack>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleEditCriteria}
          disabled={readOnly}
        >
          {hasSelectedCriteria ? t('Edit criteria') : t('Customize criteria')}
        </Button>
      </Box>
    </SchemaProvider>
  );
};

const getValuesfromAutocomplete = (
  autocompleteData: (string | undefined | null)[] | undefined,
) => {
  if (autocompleteData != null && autocompleteData.length > 0) {
    const [first, ...rest] = autocompleteData.filter(v => v != null);

    return `${first} ${rest.length > 0 ? `+${rest.length}` : ''}`;
  }

  return null;
};

const AutoMatchingChip: React.FC<{ filter: SearchQuickFilter; where: any }> = ({
  filter,
  where,
}) => {
  const { t, locale } = useLocale();
  const { getTypeFromPath, getValueFromPath } = useSchema();
  const type = getTypeFromPath(filter.path);
  const value = getValueFromPath(filter.path, where);

  const { data: placesData } = useQuery(GET_AUTO_FILTER_PLACES, {
    variables: {
      // Work with both includeParentsPlaces and not. For the new includeParentsPlaces
      // format, _or[1].parent_places.id._in holds only the user-selected IDs.
      // Falls back to _or[0].id._in for filters saved with the old format.
      ids: extractPlacesSelectedIds(value, true, true),
    },
    skip: type?.kind !== 'INPUT_OBJECT' || type.name !== 'places_bool_exp',
  });
  const { data: usersData } = useQuery(GET_AUTO_FILTER_USERS, {
    variables: {
      ids: value?.id?._in ?? [],
    },
    skip: type?.kind !== 'INPUT_OBJECT' || type.name !== 'users_bool_exp',
  });
  const { data: teamsData } = useQuery(GET_AUTO_FILTER_TEAMS, {
    variables: {
      ids: value?.id?._in ?? [],
    },
    skip: type?.kind !== 'INPUT_OBJECT' || type.name !== 'teams_bool_exp',
  });

  const { data: enumsData } = useQuery(GET_AUTO_FILTER_ENUMS);
  if (!type) {
    return null;
  }

  let valueStr: string | null = null;

  switch (type.kind) {
    case 'INPUT_OBJECT':
      {
        switch (type.name) {
          case 'Boolean_comparison_exp': {
            const {
              defaultTrueChecked,
              defaultFalseChecked,
              defaultNullChecked,
            } = getBooleanDefaultValue(value);

            const selectedValues: string[] = [];

            if (defaultTrueChecked) {
              selectedValues.push(t('True'));
            } else if (defaultFalseChecked) {
              selectedValues.push(t('False'));
            } else if (defaultNullChecked) {
              selectedValues.push(t('Null'));
            }

            valueStr =
              selectedValues.length === 0 || selectedValues.length === 3
                ? null
                : selectedValues.join(', ');
            break;
          }
          case 'timestamptz_comparison_exp':
          case 'date_comparison_exp': {
            const { startDate, endDate } = extractDateRangeValues(
              value,
              type.name === 'timestamptz_comparison_exp',
            );

            const labelValueStart = startDate
              ? format(startDate, 'd MMM yyyy')
              : '...';
            const labelValueEnd = endDate
              ? format(endDate, 'd MMM yyyy')
              : t('Now');
            const labelValue =
              startDate || endDate
                ? `${labelValueStart} - ${labelValueEnd}`
                : null;

            if (labelValue) {
              valueStr = labelValue;
            }
            break;
          }
          case 'float8_comparison_exp':
          case 'Int_comparison_exp':
          case 'Float_comparison_exp': {
            const { minVal, maxVal } = getNumberDefaultValue(value);

            if (maxVal != null && minVal == null) {
              valueStr = `≤ ${maxVal.toLocaleString(locale)}`;
            }

            if (maxVal == null && minVal != null) {
              valueStr = `≥ ${minVal.toLocaleString(locale)}`;
            }

            if (maxVal != null && minVal != null) {
              valueStr = `${minVal.toLocaleString(
                locale,
              )} - ${maxVal.toLocaleString(locale)}`;
            }
            break;
          }
          case 'places_bool_exp': {
            const selectedPlaces = placesData?.places.map(
              pl => pl.label ?? pl.label_en ?? pl.name,
            );

            valueStr = getValuesfromAutocomplete(selectedPlaces);
            break;
          }
          case 'users_bool_exp': {
            const selectedUsers = usersData?.selected_users.map(
              user => user.full_name,
            );

            valueStr = getValuesfromAutocomplete(selectedUsers);
            break;
          }
          case 'teams_bool_exp': {
            const selectedTeams = teamsData?.selected_teams.map(
              team => team.name,
            );

            valueStr = getValuesfromAutocomplete(selectedTeams);
            break;
          }
          case 'lots_bool_exp':
          default:
            // We don't handle lots here.
            return null;
        }
      }
      break;
    case 'SCALAR':
      return <AutoMatchingStringChip filter={filter} defaultValue={value} />;
    case 'ENUM': {
      const tableName = type.name.replace(/_enum$/, '');
      const defaultOptions =
        (value as string[] | undefined)?.map(val => {
          const dictionaryLabel = getDictionaryLabel(
            `${tableName}.${val}`,
            enumsData?.dictionaries,
            val,
          );
          return dictionaryLabel.length > 0 ? dictionaryLabel : val;
        }) ?? undefined;

      valueStr = getValuesfromAutocomplete(defaultOptions);
      break;
    }
  }

  if (valueStr == null) {
    return null;
  }

  return (
    <Chip
      variant="outlined"
      label={`${filter.label}: ${valueStr}`}
      size="medium"
      sx={filterChipSx}
    />
  );
};

const AutoMatchingStringChip: React.FC<{
  filter: SearchQuickFilter;
  defaultValue: string[] | undefined;
}> = ({ filter, defaultValue }) => {
  const { getTypeFromPath } = useSchema();
  const parentPath = filter.path.slice(0, -2);
  const parentType = getTypeFromPath(parentPath);
  if (!parentType) {
    throw new Error('Parent type not found');
  }
  const tableName = parentType?.name.split('_bool_exp')[0];
  const columnName = filter.path[filter.path.length - 2];

  const { options, translatedDefaultValue } = useStringFilterTranslation({
    tableName,
    columnName,
    displayedColumn: filter.displayedColumn,
    defaultValue,
    filter: filter.filter,
    addWhereClause,
    orderBy: filter.orderBy,
  });

  let valueStr: string | null = null;

  if (translatedDefaultValue && translatedDefaultValue.length > 0) {
    const translatedValues = translatedDefaultValue.map(value => {
      const option = options.find(o => o.value === value);
      return option ? option.label : value;
    });

    valueStr = getValuesfromAutocomplete(translatedValues);
  }

  if (valueStr == null) {
    return null;
  }

  return (
    <Chip
      variant="outlined"
      label={`${filter.label}: ${valueStr}`}
      size="medium"
      sx={filterChipSx}
    />
  );
};
